/**
 * Fiche produit : estimation de livraison (<delivery-timeline>) et bundle 2e article (<cro-bundle>).
 * Fichier autonome, chargé par sections/main-product.liquid. Il s'appuie uniquement sur les événements
 * publics du thème Stretch (variant:change, cart:prepare-bundled-sections, cart:change, toast:show...).
 */
(() => {
  'use strict';

  const routesRoot = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const lang = () => (document.documentElement.lang || 'fr').split('-')[0] || 'fr';

  /* ------------------------------------------------------------------------------------------------
   * Utilitaires
   * ---------------------------------------------------------------------------------------------- */

  function formatMoney(cents, format, currency, { stripZeros = false } = {}) {
    cents = Math.round(Number(cents) || 0);

    if (!format) {
      try {
        const locale = document.documentElement.lang || 'fr-FR';
        const options = { style: 'currency', currency: currency || 'EUR' };
        if (stripZeros && cents % 100 === 0) {
          options.minimumFractionDigits = 0;
          options.maximumFractionDigits = 0;
        }
        return new Intl.NumberFormat(locale, options).format(cents / 100);
      } catch (error) {
        format = '€{{amount_with_comma_separator}}';
      }
    }

    format = String(format).replace(/<[^>]*>/g, '');
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) {
      return format;
    }

    const build = (precision, thousands = ',', decimal = '.') => {
      const fixed = (cents / 100).toFixed(precision);
      const [integer, decimals] = fixed.split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? grouped + decimal + decimals : grouped;
    };

    let value;
    switch (placeholder[1]) {
      case 'amount_no_decimals': value = build(0); break;
      case 'amount_with_comma_separator': value = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = build(0, '.', ','); break;
      case 'amount_with_space_separator': value = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = build(0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = build(2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = build(2, "'", '.'); break;
      default: value = build(2);
    }

    if (stripZeros && cents % 100 === 0) {
      value = value.replace(/[.,]00$/, '');
    }

    return format.replace(placeholder[0], value);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  // "37", "37.0", "37,0", "EU 37" => "37" ; "M" => "m"
  function normalizeOptionValue(value) {
    const raw = String(value ?? '').trim().toLowerCase().replace(',', '.');
    const numeric = raw.match(/^[a-z\s]*(\d+(?:\.\d+)?)\s*[a-z]*$/);
    return numeric ? String(parseFloat(numeric[1])) : raw;
  }

  const SIZE_OPTION_REGEX = /taille|pointure|size|gr(ö|o)(ß|ss)e|taglia|talla|maat/i;

  function findSizeOptionIndex(optionNames) {
    if (!Array.isArray(optionNames) || optionNames.length === 0) return -1;
    const index = optionNames.findIndex((name) => SIZE_OPTION_REGEX.test(typeof name === 'string' ? name : name?.name || ''));
    if (index !== -1) return index;
    return optionNames.length === 1 ? 0 : -1;
  }

  /* ------------------------------------------------------------------------------------------------
   * <delivery-timeline>
   * ---------------------------------------------------------------------------------------------- */

  const DAY = 86400000;

  class DeliveryTimeline extends HTMLElement {
    connectedCallback() {
      this.render();
      this.timer = window.setInterval(() => this.render(), 60 * 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    get cutoff() { return parseInt(this.dataset.cutoffHour, 10) || 23; }
    get shipWeekends() { return this.dataset.shipWeekends === 'true'; }
    get businessDays() { return this.dataset.businessDays === 'true'; }

    get closedDates() {
      return new Set((this.dataset.closedDates || '').split(',').map((date) => date.trim()).filter(Boolean));
    }

    nowParts() {
      const now = new Date();
      try {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: this.dataset.timezone || 'Europe/Paris',
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
        }).formatToParts(now);
        const get = (type) => parseInt(parts.find((part) => part.type === type).value, 10);
        return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24 };
      } catch (error) {
        return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(), h: now.getHours() };
      }
    }

    static iso(date) {
      return date.toISOString().slice(0, 10);
    }

    isShippingDay(date) {
      const weekday = date.getUTCDay();
      if (!this.shipWeekends && (weekday === 0 || weekday === 6)) return false;
      return !this.closedDates.has(DeliveryTimeline.iso(date));
    }

    addDeliveryDays(start, days) {
      if (!this.businessDays) {
        return new Date(start.getTime() + days * DAY);
      }

      let date = start;
      let added = 0;
      let guard = 0;

      while (added < days && guard++ < 400) {
        date = new Date(date.getTime() + DAY);
        const weekday = date.getUTCDay();
        if (weekday !== 0 && weekday !== 6) added++;
      }

      return date;
    }

    formatShort(date) {
      const locale = document.documentElement.lang || 'fr-FR';
      const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date).replace('.', '');
      const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(date);
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${date.getUTCDate()} ${month}`;
    }

    relativeDay(diff, date) {
      const locale = document.documentElement.lang || 'fr-FR';
      if (diff <= 1) {
        try {
          return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(diff, 'day').replace('’', "'");
        } catch (error) {
          return diff === 0 ? "aujourd'hui" : 'demain';
        }
      }
      return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
    }

    render() {
      const { y, m, d, h } = this.nowParts();
      const today = new Date(Date.UTC(y, m - 1, d));
      let ship = today;

      if (!this.isShippingDay(today) || h >= this.cutoff) {
        let guard = 0;
        do {
          ship = new Date(ship.getTime() + DAY);
        } while (!this.isShippingDay(ship) && guard++ < 60);
      }

      let minDays = parseInt(this.dataset.minDeliveryDays, 10) || 0;
      let maxDays = parseInt(this.dataset.maxDeliveryDays, 10) || minDays;
      if (minDays > maxDays) [minDays, maxDays] = [maxDays, minDays];

      const minDate = this.addDeliveryDays(ship, minDays);
      const maxDate = this.addDeliveryDays(ship, maxDays);
      const diff = Math.round((ship - today) / DAY);
      const shipWord = this.relativeDay(diff, ship);
      const cutoffShort = `${this.cutoff}h`;
      const cutoffLong = `${this.cutoff}h00`;

      const headline = (this.dataset.headline || '').replace('[heure]', cutoffShort).replace('[jour]', shipWord);
      let orderLabel;

      if (diff === 0) {
        orderLabel = (this.dataset.orderBefore || '').replace('[heure]', cutoffLong);
      } else {
        const todayWord = this.relativeDay(0, today);
        orderLabel = todayWord.charAt(0).toUpperCase() + todayWord.slice(1);
      }

      const deliveryLabel = minDays === maxDays
        ? this.formatShort(minDate)
        : `${this.formatShort(minDate)} – ${this.formatShort(maxDate)}`;

      this.setText('[data-delivery-headline]', headline);
      this.setText('[data-order-date]', orderLabel);
      this.setText('[data-shipping-date]', this.formatShort(ship));
      this.setText('[data-delivery-date]', deliveryLabel);
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element && value && element.textContent !== value) {
        element.textContent = value;
      }
    }
  }

  if (!window.customElements.get('delivery-timeline')) {
    window.customElements.define('delivery-timeline', DeliveryTimeline);
  }

  /* ------------------------------------------------------------------------------------------------
   * <cro-bundle>
   * ---------------------------------------------------------------------------------------------- */

  class CroBundle extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('script[data-bundle-json]').textContent);
      } catch (error) {
        console.warn('[cro-bundle] Données du bundle illisibles', error);
        return;
      }

      if (!this.data.offers || this.data.offers.length === 0) {
        return;
      }

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.percent = parseFloat(this.dataset.percent) || 0;
      this.mode = this.dataset.mode === 'single' ? 'single' : 'bundle';
      this.offerIndex = 0;
      this.userPickedSize = false;
      this.mainSizeIndex = findSizeOptionIndex(this.data.main.options);
      this.mainVariant = this.findMainVariant(this.form?.elements?.id?.value);

      this.addEventListener('click', this.onClick.bind(this), { signal });
      this.addEventListener('keydown', this.onKeydown.bind(this), { signal });

      document.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });
      // Phase de capture : on passe avant le gestionnaire du thème, uniquement en mode bundle
      document.addEventListener('submit', this.onSubmit.bind(this), { capture: true, signal });

      this.selectOffer(0, { focus: false });
      this.applyMode();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.productInfo?.removeAttribute('data-cro-bundle-mode');
    }

    /* ---------- Getters ---------- */

    get form() {
      return document.getElementById(this.dataset.formId);
    }

    get productInfo() {
      return this.closest('.product-info') || this.closest('product-rerender');
    }

    get currentOffer() {
      return this.data.offers[this.offerIndex];
    }

    money(cents, options) {
      return formatMoney(cents, this.dataset.moneyFormat, this.dataset.currency, options);
    }

    findMainVariant(id) {
      const variants = this.data.main.variants || [];
      return variants.find((variant) => String(variant.id) === String(id)) || variants.find((variant) => variant.available) || variants[0] || null;
    }

    /* ---------- Offre (2e article) ---------- */

    selectedOfferVariant() {
      const offer = this.currentOffer;
      if (!offer || !this.selected || this.selected.some((value) => value == null)) return null;
      return offer.variants.find((variant) => variant.options.every((value, index) => value === this.selected[index])) || null;
    }

    // Variante utilisée pour afficher un prix tant que la taille n'est pas choisie
    displayOfferVariant(offer = this.currentOffer, index = this.offerIndex) {
      if (index === this.offerIndex) {
        const selected = this.selectedOfferVariant();
        if (selected) return selected;
      }
      return offer.variants.find((variant) => variant.available) || offer.variants[0];
    }

    discountFor(price) {
      return Math.round((price * this.percent) / 100);
    }

    isValueAvailable(optionIndex, value) {
      return this.currentOffer.variants.some((variant) =>
        variant.available &&
        variant.options[optionIndex] === value &&
        variant.options.every((other, index) => index === optionIndex || this.selected[index] == null || other === this.selected[index])
      );
    }

    existsAvailable(optionIndex, value) {
      return this.currentOffer.variants.some((variant) => variant.available && variant.options[optionIndex] === value);
    }

    syncSelectionFromMain() {
      const offer = this.currentOffer;
      const options = offer.options || [];
      const firstAvailable = offer.variants.find((variant) => variant.available) || offer.variants[0];
      const sizeIndex = findSizeOptionIndex(options);

      this.selected = options.map((option, index) => {
        if (option.values.length === 1) return option.values[0];
        if (index === sizeIndex) return null;
        return firstAvailable ? firstAvailable.options[index] : null;
      });

      if (sizeIndex !== -1 && options[sizeIndex].values.length > 1) {
        const mainSize = this.mainVariant && this.mainSizeIndex !== -1 ? this.mainVariant.options[this.mainSizeIndex] : null;

        if (mainSize != null) {
          const wanted = normalizeOptionValue(mainSize);
          const match = options[sizeIndex].values.find((value) => normalizeOptionValue(value) === wanted && this.isValueAvailable(sizeIndex, value));
          this.selected[sizeIndex] = match ?? null;
        }
      }

      // Une seule variante disponible : on la présélectionne
      const available = offer.variants.filter((variant) => variant.available);
      if (available.length === 1 && this.selected.some((value) => value == null)) {
        this.selected = [...available[0].options];
      }
    }

    selectOffer(index, { focus = true } = {}) {
      if (!this.data.offers[index]) return;

      this.offerIndex = index;
      this.userPickedSize = false;

      this.querySelectorAll('[data-offer-index]').forEach((card) => {
        const isSelected = Number(card.dataset.offerIndex) === index;
        card.setAttribute('aria-checked', String(isSelected));
        card.tabIndex = isSelected ? 0 : -1;

        if (isSelected && focus) {
          card.focus({ preventScroll: true });
          this.scrollCardIntoView(card);
        }
      });

      this.syncSelectionFromMain();
      this.hideError();
      this.renderOptions();
      this.updatePrices();
    }

    scrollCardIntoView(card) {
      const track = card.parentElement;
      if (!track) return;
      const left = card.offsetLeft - track.offsetLeft;
      const right = left + card.offsetWidth;

      if (left < track.scrollLeft) {
        track.scrollTo({ left, behavior: 'smooth' });
      } else if (right > track.scrollLeft + track.clientWidth) {
        track.scrollTo({ left: right - track.clientWidth, behavior: 'smooth' });
      }
    }

    renderOptions() {
      const area = this.querySelector('[data-size-area]');
      if (!area) return;

      const offer = this.currentOffer;
      const options = (offer.options || []).filter((option) => option.values.length > 1);
      const swatchClass = this.dataset.swatchClass || 'block-swatch';

      if (options.length === 0) {
        area.hidden = true;
        area.innerHTML = '';
        return;
      }

      area.hidden = false;
      const multiple = options.length > 1;

      area.innerHTML = offer.options.map((option, optionIndex) => {
        if (option.values.length <= 1) return '';

        const label = multiple ? option.name : (this.dataset.sizeLabel || option.name);
        const current = this.selected[optionIndex];

        const buttons = option.values.map((value) => {
          const isSelected = value === current;
          const exists = this.existsAvailable(optionIndex, value);
          const availableWithSelection = this.isValueAvailable(optionIndex, value);
          const classes = [swatchClass, isSelected ? 'is-selected' : '', !availableWithSelection ? 'is-disabled' : ''].filter(Boolean).join(' ');

          return `<button type="button" class="${classes}" data-option-index="${optionIndex}" data-value="${escapeHtml(value)}" aria-pressed="${isSelected}" ${exists ? '' : 'disabled aria-disabled="true"'}><span>${escapeHtml(value)}</span></button>`;
        }).join('');

        return `
          <div class="cro-bundle__size-group">
            <p class="cro-bundle__size-label">${escapeHtml(label)} : <strong data-size-value="${optionIndex}">${escapeHtml(current ?? this.dataset.chooseLabel ?? '')}</strong></p>
            <div class="cro-options" role="group" aria-label="${escapeHtml(label)}">${buttons}</div>
          </div>`;
      }).join('');
    }

    selectOptionValue(optionIndex, value) {
      const offer = this.currentOffer;
      this.selected[optionIndex] = value;
      this.userPickedSize = true;

      // Si la combinaison n'existe pas / n'est pas dispo, on bascule sur une variante dispo qui contient cette valeur
      const variant = this.selectedOfferVariant();
      if (!variant || !variant.available) {
        const fallback = offer.variants.find((item) => item.available && item.options[optionIndex] === value);
        if (fallback) {
          this.selected = this.selected.map((current, index) => (current == null && index !== optionIndex ? null : fallback.options[index]));
        }
      }

      this.hideError();
      this.renderOptions();
      this.updatePrices();

      const button = this.querySelector(`[data-option-index="${optionIndex}"][data-value="${CSS.escape(value)}"]`);
      button?.focus({ preventScroll: true });
    }

    /* ---------- Mode ---------- */

    setMode(mode, { focus = false } = {}) {
      if (mode !== 'single' && mode !== 'bundle') return;
      this.mode = mode;
      this.applyMode();

      if (focus) {
        this.querySelector(`[data-mode-option="${mode}"]`)?.focus();
      }
    }

    applyMode() {
      this.dataset.mode = this.mode;

      this.querySelectorAll('[data-mode-option]').forEach((option) => {
        const isSelected = option.dataset.modeOption === this.mode;
        option.setAttribute('aria-checked', String(isSelected));
        option.tabIndex = isSelected ? 0 : -1;
      });

      const wrap = this.querySelector('[data-offer-wrap]');
      if (wrap) {
        wrap.inert = this.mode !== 'bundle';
      }

      // Utilisé en CSS pour masquer le bouton "Acheter maintenant" (il n'achèterait qu'un seul article)
      this.productInfo?.setAttribute('data-cro-bundle-mode', this.mode);
      this.hideError();
    }

    /* ---------- Prix ---------- */

    updatePrices() {
      const mainPrice = this.mainVariant ? this.mainVariant.price : 0;
      const offerVariant = this.displayOfferVariant();
      const offerPrice = offerVariant ? offerVariant.price : 0;
      const discount = this.discountFor(offerPrice);

      this.setText('[data-single-price]', this.money(mainPrice));
      this.setText('[data-bundle-compare]', this.money(mainPrice + offerPrice));
      this.setText('[data-bundle-total]', this.money(mainPrice + offerPrice - discount));
      this.setText('[data-bundle-savings]', `${this.dataset.savingsLabel || ''} ${this.money(discount, { stripZeros: true })}`.trim());

      this.querySelectorAll('[data-offer-index]').forEach((card) => {
        const index = Number(card.dataset.offerIndex);
        const offer = this.data.offers[index];
        if (!offer) return;
        const variant = this.displayOfferVariant(offer, index);
        if (!variant) return;
        const compare = card.querySelector('[data-card-compare]');
        const price = card.querySelector('[data-card-price]');
        if (compare) compare.textContent = this.money(variant.price);
        if (price) price.textContent = this.money(variant.price - this.discountFor(variant.price));
      });
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    }

    /* ---------- Événements ---------- */

    onClick(event) {
      const modeOption = event.target.closest('[data-mode-option]');
      if (modeOption && this.contains(modeOption)) {
        this.setMode(modeOption.dataset.modeOption);
        return;
      }

      const card = event.target.closest('[data-offer-index]');
      if (card && this.contains(card)) {
        // Choisir un article active automatiquement le bundle
        if (this.mode !== 'bundle') this.setMode('bundle');
        this.selectOffer(Number(card.dataset.offerIndex));
        return;
      }

      const optionButton = event.target.closest('[data-option-index][data-value]');
      if (optionButton && this.contains(optionButton) && !optionButton.disabled) {
        this.selectOptionValue(Number(optionButton.dataset.optionIndex), optionButton.dataset.value);
      }
    }

    onKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (!keys.includes(event.key)) return;

      const modeOption = event.target.closest('[data-mode-option]');
      const card = event.target.closest('[data-offer-index]');
      const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;

      if (modeOption) {
        event.preventDefault();
        this.setMode(this.mode === 'single' ? 'bundle' : 'single', { focus: true });
      } else if (card) {
        event.preventDefault();
        const total = this.data.offers.length;
        this.selectOffer((this.offerIndex + delta + total) % total);
      }
    }

    onVariantChange(event) {
      if (event.target !== this.form) return;

      const variant = event.detail?.variant;
      this.mainVariant = (variant && variant.id && this.findMainVariant(variant.id)) || (variant && variant.id ? variant : this.mainVariant);

      if (!this.userPickedSize) {
        this.syncSelectionFromMain();
        this.renderOptions();
      }

      this.updatePrices();
    }

    async onSubmit(event) {
      const form = this.form;

      if (this.mode !== 'bundle' || !form || event.target !== form) {
        return;
      }

      // On prend la main : le gestionnaire du thème n'ajoutera pas l'article seul
      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.submitter?.getAttribute('aria-busy') === 'true') {
        return;
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const offerVariant = this.selectedOfferVariant();

      if (!offerVariant) {
        this.showError(this.dataset.sizeError);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.querySelector('[data-size-area] button:not([disabled])')?.focus({ preventScroll: true });
        return;
      }

      if (!offerVariant.available) {
        this.showError(this.dataset.unavailableError);
        return;
      }

      const formData = new FormData(form);
      const mainId = Number(formData.get('id'));
      const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') {
          properties[match[1]] = value;
        }
      }

      const items = [
        { id: mainId, quantity, properties },
        { id: offerVariant.id, quantity: 1, properties: { _bundle: this.dataset.bundleProperty || 'Bundle' } }
      ];

      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
      const sections = [...sectionsToBundle].join(',');

      const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
      const showLoadingBar = event.submitter ? event.submitter.querySelector('button-content') === null : true;

      submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      if (showLoadingBar) document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(`${routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          this.handleError(form, responseJson.description || responseJson.message);
          return;
        }

        let cart = null;
        const code = (this.dataset.code || '').trim();

        if (code) {
          cart = await this.applyDiscountCode(code, sections);
        }

        if (!cart) {
          cart = await (await fetch(`${routesRoot()}cart.js`)).json();
          cart.sections = responseJson.sections;
        } else if (!cart.sections) {
          cart.sections = responseJson.sections;
        }

        const settings = window.themeVariables?.settings || {};

        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = `${routesRoot()}cart`;
          return;
        }

        const addedItems = responseJson.items || [];

        form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));

        const addedLabel = window.themeVariables?.strings?.addedToCart || '{{product_title}}';

        if (settings.cartType === 'message') {
          document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: addedLabel.replace('{{product_title}}', addedItems[0]?.title || '') } }));
        } else {
          const status = document.getElementById('status-announcement');
          if (status) status.textContent = addedItems.map((item) => addedLabel.replace('{{product_title}}', item.title || '')).join(', ');
        }
      } catch (error) {
        console.error('[cro-bundle]', error);
        this.handleError(form, this.dataset.genericError);
      } finally {
        submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
        if (showLoadingBar) document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    async applyDiscountCode(code, sections) {
      try {
        const currentCart = await (await fetch(`${routesRoot()}cart.js`)).json();
        const codes = (currentCart.discount_codes || []).filter((discount) => discount.applicable).map((discount) => discount.code);

        if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
          codes.push(code);
        }

        const response = await fetch(`${routesRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections })
        });

        return response.ok ? await response.json() : null;
      } catch (error) {
        return null;
      }
    }

    handleError(form, message) {
      const text = message || this.dataset.genericError;
      this.showError(text);
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: text } }));
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: text, tone: 'error' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    showError(message) {
      const error = this.querySelector('[data-bundle-error]');
      if (!error || !message) return;
      error.textContent = message;
      error.hidden = false;
    }

    hideError() {
      const error = this.querySelector('[data-bundle-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
    }
  }

  if (!window.customElements.get('cro-bundle')) {
    window.customElements.define('cro-bundle', CroBundle);
  }
})();
